import { Activity } from 'lucide-react';
import { useActiveRuns } from '../api/queries';
import type { RunSummary } from '../api/types';
import { waitingWords } from '../networks/shared';
import { strings } from '../strings';
import { MenuButton, type MenuEntry } from './Menu';

const s = strings.shell.activeRuns;
const n = strings.networks;

/** "Plan · dev · Orders", or for a network run "Apply · Production" with "Attachment" for attachment runs. */
function itemLabel(run: RunSummary): string {
  const kind = strings.status.runKind[run.kind];
  if (run.networkId && !run.deploymentId) {
    const label = n.runs.pillItem(kind, run.networkName ?? n.nav.kind);
    return run.scope === 'attachment' ? `${label} · ${n.runLabels.attachment}` : label;
  }
  return s.item(kind, run.deploymentName, run.blueprintName);
}

/**
 * The active-runs pill of the top bar (spec 6.1.7): shown only while runs are active, its menu lists them with
 * deployment, blueprint and progress and links to each. Data: the polled `GET /runs?status=active`.
 */
export function ActiveRunsPill() {
  const { data } = useActiveRuns();
  const runs = data?.items ?? [];
  if (runs.length === 0) return null;

  const entries: MenuEntry[] = [
    ...runs.map<MenuEntry>((run) => ({
      kind: 'link',
      id: run.id,
      to: `/runs/${run.id}`,
      label: (
        <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
          <span className="truncate">{itemLabel(run)}</span>
          <span className="shrink-0 font-mono text-[12px] text-fg-muted">
            {run.status === 'pending'
              ? run.waitingFor
                ? `${s.queued} · ${waitingWords(run.waitingFor)}`
                : s.queued
              : run.progress && run.progress.total > 0
                ? s.progress(run.progress.completed, run.progress.total)
                : strings.status.run.running}
          </span>
        </span>
      ),
    })),
    { kind: 'separator', id: 'sep' },
    { kind: 'link', id: 'all', label: s.all, to: '/runs?status=active' },
  ];

  return (
    <MenuButton
      buttonClassName="runs-pill"
      buttonContent={
        <>
          <Activity aria-hidden className="size-3.5" />
          {s.button(runs.length)}
        </>
      }
      menuLabel={s.menu}
      entries={entries}
      placement="below-end"
    />
  );
}
